import { Link } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const features = [
  {
    icon: "ri-film-line",
    title: "只推荐看完的短片",
    description: "每一部上架的短片都由编辑完整看过，标注导演、时长与类型，不做标题党。",
  },
  {
    icon: "ri-newspaper-line",
    title: "资讯写清楚来源",
    description: "行业新闻、影评与活动报道分开归类，正文写明作者与发布时间，方便你判断可信度。",
  },
  {
    icon: "ri-bookmark-line",
    title: "收藏不需要登录",
    description: "收藏记录保存在你自己的浏览器里，不需要注册账号，也不会被用来推送广告。",
  },
  {
    icon: "ri-search-eye-line",
    title: "搜索与筛选是前端演示",
    description: "本站为内容演示站点，搜索结果与筛选均基于站内示例数据，不连接真实内容库。",
  },
];

const stats = [
  { label: "上线时间", value: "2024" },
  { label: "精选短片", value: "14 部" },
  { label: "资讯累计", value: "10 条" },
  { label: "更新频率", value: "每周 3 次" },
];

export default function AboutPage() {
  useDocumentMeta(
    "关于我们 - 小电影影视短片平台",
    "了解小电影的定位、内容标准与联系方式。我们专注影视短片推荐与影视资讯整理，让好作品被更多人看见。",
    "关于小电影,小电影团队,影视短片平台,联系我们",
  );

  return (
    <PageShell>
      <section className="bg-background-100/70 border-b border-background-200">
        <div className="container-page py-6 md:py-8">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <h1 className="mt-3 text-2xl md:text-3xl font-extrabold tracking-tight text-foreground-950">
            关于小电影
          </h1>
          <p className="mt-2 max-w-2xl text-sm md:text-base leading-relaxed text-foreground-600">
            一个把「短片」当正经内容做的小站点。我们相信二十分钟足够讲一个好故事，也足够让你重新抬头看看周围。
          </p>
        </div>
      </section>

      <div className="container-page py-8 md:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-7 lg:gap-9">
          <div className="lg:col-span-3">
            <div className="w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20cinematic%20photograph%20of%20a%20small%20independent%20film%20screening%20room%20with%20wooden%20chairs%20and%20a%20glowing%20projector%20beam%2C%20dust%20particles%20in%20the%20light%2C%20soft%20amber%20ambience%2C%20intimate%20creative%20atmosphere%2C%20editorial%20style&width=1200&height=675&seq=about-studio-c1&orientation=landscape"
                alt="小电影放映空间"
                title="小电影 - 关于我们"
                className="w-full h-full object-cover object-top"
              />
            </div>

            <h2 className="mt-7 text-xl font-extrabold text-foreground-950">我们为什么做这件事</h2>
            <div className="mt-3 space-y-4 text-[14px] md:text-[15px] leading-[1.9] text-foreground-700">
              <p>
                短片是这个时代最被低估的内容形式。它短，但不轻；它便宜，但不粗糙。很多创作者把最锐利的想法留在了短片里，
                却因为缺少一个稳定的展示入口，被淹没在信息流里。
              </p>
              <p>
                小电影想做的，就是把入口整理干净。我们用固定的标准挑选作品：完整看完、确认资料、写清一句话简介。
                资讯部分同样克制——只写发生的事，只在能核实的时候发布。
              </p>
              <p>
                网站目前是一个内容演示项目，所有短片、资讯、图片与人名均为虚构示例，用于展示浏览、筛选、搜索、收藏与分享的完整流程。
                我们不提供在线播放和下载，也不会用虚假的播放按钮误导你。
              </p>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="text-sm font-bold text-foreground-950 mb-4">快速了解</h2>
              <dl className="grid grid-cols-2 gap-4">
                {stats.map((stat) => (
                  <div key={stat.label}>
                    <dt className="text-[11px] text-foreground-500">{stat.label}</dt>
                    <dd className="mt-1 font-label text-lg font-extrabold text-foreground-950">
                      {stat.value}
                    </dd>
                  </div>
                ))}
              </dl>
              <div className="mt-5 pt-4 border-t border-background-200">
                <h3 className="text-[13px] font-semibold text-foreground-800 mb-1.5">内容更正</h3>
                <p className="text-[12px] leading-relaxed text-foreground-600">
                  如果发现资料有误，欢迎邮件告知，我们会在核实后更新。
                </p>
              </div>
            </div>

            <div className="mt-5 rounded-lg bg-primary-500 p-5 text-background-50">
              <p className="text-[11px] tracking-[0.2em] uppercase text-primary-100">Contact</p>
              <h2 className="mt-2 text-lg font-extrabold">联系我们</h2>
              <p className="mt-2 text-[13px] leading-relaxed text-primary-100">
                内容合作、作品推荐、资料更正，都可以直接发邮件给我们。
              </p>
              <a
                href="mailto:hello@xiaodianying.studio"
                className="mt-4 inline-flex items-center gap-2 h-9 px-4 rounded-md bg-background-50 text-primary-700 text-[13px] font-semibold hover:bg-accent-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-mail-line" />
                hello@xiaodianying.studio
              </a>
              <p className="mt-3 text-[11px] text-primary-200">工作日 48 小时内回复</p>
            </div>
          </div>
        </div>

        <section className="mt-10 md:mt-12">
          <h2 className="text-xl font-extrabold text-foreground-950">我们的四条原则</h2>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <span className="w-9 h-9 rounded-md bg-accent-100 flex items-center justify-center">
                  <i className={`${feature.icon} text-accent-700`} />
                </span>
                <h3 className="mt-3 text-[15px] font-bold text-foreground-950">{feature.title}</h3>
                <p className="mt-1.5 text-[13px] leading-relaxed text-foreground-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-9 rounded-lg border border-background-200 bg-background-100 p-5 md:p-7">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h2 className="text-base font-bold text-foreground-950">合作说明</h2>
              <p className="mt-2 text-[13px] leading-relaxed text-foreground-600">
                我们接受短片作品自荐与影展合作，不接软文式片单。所有合作内容会明确标注来源，
                并且不会影响编辑推荐的结果。
              </p>
            </div>
            <div>
              <h2 className="text-base font-bold text-foreground-950">版权声明</h2>
              <p className="mt-2 text-[13px] leading-relaxed text-foreground-600">
                站内文字为原创示例内容，图片由演示工具生成，仅用于界面展示。未经许可，请勿将示例内容用于商业用途。
                如需转载，请先邮件联系。
              </p>
            </div>
          </div>
          <div className="mt-6 pt-5 border-t border-background-300 flex flex-wrap items-center justify-between gap-3">
            <p className="text-[11px] text-foreground-500">
              © 2026 小电影 XiaoDianYing Studio · 演示站点
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-primary-500 text-background-50 text-[13px] font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-4-line" />
              返回首页
            </Link>
          </div>
        </section>
      </div>
    </PageShell>
  );
}